import { useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { CustomFieldInputs } from "@/components/CustomFieldInputs";
import { PageHeader } from "@/components/PageHeader";
import { DataTable } from "@/components/DataTable";
import { LoadingState, ErrorState } from "@/components/LoadingError";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useCustomFieldDefinitions } from "@/hooks/useCustomFieldDefinitions";
import type { CustomFieldDefinition } from "@/types/customFields";
import { apiFetch } from "@/utils/api";

interface LocationRow {
  id: string;
  name: string;
  customFields?: Record<string, string>;
}

function initCustomFieldValues(
  definitions: CustomFieldDefinition[],
  existing?: Record<string, string>,
): Record<string, string> {
  const values: Record<string, string> = {};
  for (const def of definitions) {
    values[def.fieldKey] =
      existing?.[def.fieldKey] ?? (def.fieldType === "boolean" ? "false" : "");
  }
  return values;
}

export function AdminLocationsPage() {
  const [locations, setLocations] = useState<LocationRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState("");
  const [customFieldValues, setCustomFieldValues] = useState<Record<string, string>>({});
  const [editingId, setEditingId] = useState<string | null>(null);
  const { definitions: customFields, loading: fieldsLoading } = useCustomFieldDefinitions("location");

  const load = async () => {
    const data = await apiFetch<{ locations: LocationRow[] }>("/locations");
    setLocations(data.locations);
  };

  useEffect(() => {
    load()
      .catch((e) => setError(e instanceof Error ? e.message : "Failed to load"))
      .finally(() => setLoading(false));
  }, []);

  const resetForm = () => {
    setName("");
    setCustomFieldValues(initCustomFieldValues(customFields));
    setFormError("");
  };

  const startEdit = (loc: LocationRow) => {
    setShowNew(false);
    setEditingId(loc.id);
    setName(loc.name);
    setCustomFieldValues(initCustomFieldValues(customFields, loc.customFields));
    setFormError("");
  };

  const cancelEdit = () => {
    setEditingId(null);
    resetForm();
  };

  const create = async (e: FormEvent) => {
    e.preventDefault();
    setFormError("");
    setSaving(true);
    try {
      await apiFetch("/locations", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, customFields: customFieldValues }),
      });
      setShowNew(false);
      resetForm();
      await load();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : "Failed to create location");
    } finally {
      setSaving(false);
    }
  };

  const saveEdit = async (e: FormEvent) => {
    e.preventDefault();
    if (!editingId) return;
    setFormError("");
    setSaving(true);
    try {
      await apiFetch(`/locations/${editingId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, customFields: customFieldValues }),
      });
      cancelEdit();
      await load();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : "Failed to update location");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id: string) => {
    if (!window.confirm("Delete this location?")) return;
    setFormError("");
    try {
      await apiFetch(`/locations/${id}`, { method: "DELETE" });
      if (editingId === id) cancelEdit();
      await load();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : "Failed to delete location");
    }
  };

  if (loading || fieldsLoading) return <LoadingState />;
  if (error) return <ErrorState message={error} />;

  return (
    <div className="space-y-4">
      <PageHeader
        title="Locations"
        subtitle={
          <>
            Storage locations used when receiving parts and tracking inventory.{" "}
            <Link to="/admin/custom-fields?type=location" className="underline text-[#1e3a4f]">
              Manage custom fields
            </Link>
          </>
        }
        actions={
          <Button
            type="button"
            onClick={() => {
              if (showNew) {
                setShowNew(false);
                resetForm();
              } else {
                setShowNew(true);
                setEditingId(null);
                resetForm();
              }
            }}
            className="bg-[#1e3a4f] hover:bg-[#163041]"
          >
            {showNew ? "Cancel" : "Add Location"}
          </Button>
        }
      />

      {formError && !showNew && !editingId && <p className="text-sm text-destructive">{formError}</p>}

      {showNew && (
        <form
          className="bg-white rounded-lg shadow p-4 grid gap-3 sm:grid-cols-2 max-w-xl"
          onSubmit={(e) => void create(e)}
        >
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="location-name">Location name *</Label>
            <Input
              id="location-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Warehouse A"
              required
            />
          </div>
          <CustomFieldInputs
            definitions={customFields}
            values={customFieldValues}
            onChange={(fieldKey, value) =>
              setCustomFieldValues((prev) => ({ ...prev, [fieldKey]: value }))
            }
            idPrefix="new-loc"
          />
          {formError && <p className="text-sm text-destructive sm:col-span-2">{formError}</p>}
          <Button type="submit" disabled={saving} className="w-fit bg-[#1e3a4f] hover:bg-[#163041]">
            {saving ? "Creating…" : "Create location"}
          </Button>
        </form>
      )}

      {editingId && (
        <form
          className="bg-white rounded-lg shadow p-4 grid gap-3 sm:grid-cols-2 max-w-xl border-2 border-[#1e3a4f]/20"
          onSubmit={(e) => void saveEdit(e)}
        >
          <p className="sm:col-span-2 text-sm font-semibold text-[#1e3a4f]">Edit location</p>
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="edit-location-name">Location name *</Label>
            <Input
              id="edit-location-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>
          <CustomFieldInputs
            definitions={customFields}
            values={customFieldValues}
            onChange={(fieldKey, value) =>
              setCustomFieldValues((prev) => ({ ...prev, [fieldKey]: value }))
            }
            idPrefix="edit-loc"
          />
          {formError && <p className="text-sm text-destructive sm:col-span-2">{formError}</p>}
          <div className="flex gap-2 sm:col-span-2">
            <Button type="submit" disabled={saving} className="bg-[#1e3a4f] hover:bg-[#163041]">
              {saving ? "Saving…" : "Save changes"}
            </Button>
            <Button type="button" variant="secondary" disabled={saving} onClick={cancelEdit}>
              Cancel
            </Button>
          </div>
        </form>
      )}

      <DataTable
        columns={[
          { key: "name", header: "Location", render: (loc) => loc.name },
          {
            key: "actions",
            header: "",
            render: (loc) => (
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={editingId === loc.id}
                  onClick={() => startEdit(loc)}
                >
                  Edit
                </Button>
                <Button size="sm" variant="secondary" onClick={() => void remove(loc.id)}>
                  Delete
                </Button>
              </div>
            ),
          },
        ]}
        rows={locations}
        rowKey={(loc) => loc.id}
      />
    </div>
  );
}
